import { motion } from "framer-motion";
import { ShieldCheck, MapPin, Clock, CalendarCheck, Phone, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import LeadForm from "@/components/LeadForm";
import { site } from "@/lib/site";

const trust = [
  { icon: ShieldCheck, label: "Free Estimates" },
  { icon: MapPin, label: "Summit County Local" },
  { icon: CalendarCheck, label: "Fast Scheduling" },
  { icon: Clock, label: "Mon–Sat, 7 AM–7 PM" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <img
        src="/assets/images/home-hero-fence-installation.webp"
        alt="Freshly installed white ranch-rail fence bordering a green Summit County property"
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={500}
        height={333}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(156,33%,7%/0.72)] via-[hsl(156,30%,9%/0.55)] to-[hsl(152,45%,14%/0.4)]" />
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div
        aria-hidden="true"
        className="absolute top-20 right-20 w-96 h-96 rounded-full animate-hero-glow pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.3), transparent 70%)" }}
      />

      <div className="relative z-10 container-max px-4 py-28 md:py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-primary/15 backdrop-blur-sm border border-primary/30 text-white text-sm font-semibold px-5 py-2 rounded-full mb-8"
              >
                <MapPin className="h-4 w-4 text-secondary" />
                {site.serviceArea}
              </motion.div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.02] mb-6 tracking-tight font-bold">
                Fencing Built for
                <br />
                Colorado's <span className="text-gradient-primary">High Country</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl mb-9 leading-relaxed max-w-xl">
                Summit Neighbor Fencing Co. designs, installs, and repairs wood,
                picket, chain link, and custom fences across Dillon and Summit
                County — built to stand up to mountain weather and last for years.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="gap-2 px-8 py-6 text-lg rounded-xl btn-glow shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]"
                >
                  Get Free Estimate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="gap-2 px-8 py-6 text-lg rounded-xl border-2 border-white/25 text-white bg-white/5 hover:bg-white/15 hover:text-white hover:border-white/40 backdrop-blur-sm"
                >
                  <a href={site.phoneHref}>
                    <Phone className="h-5 w-5" /> {site.phone}
                  </a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <Gift className="h-6 w-6 text-secondary shrink-0" />
                <span className="text-white/90 font-semibold text-sm">
                  Free estimates — plus{" "}
                  <span className="text-secondary font-bold">refer a neighbor & earn a reward</span>.
                </span>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl pointer-events-none -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.35), transparent 70%)" }}
            />
            <div className="relative rounded-2xl overflow-hidden shadow-premium-lg bg-card p-2 border border-border/60">
              <LeadForm instanceId="hero" defer />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-14 md:mt-16 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-center md:justify-around gap-x-8 gap-y-4">
            {trust.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-primary/25 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-secondary" />
                </div>
                <span className="text-white/90 text-sm font-semibold">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-16 bg-background leading-none">
        <svg viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="block w-full h-full" preserveAspectRatio="none">
          <path d="M0 32C240 64 480 64 720 32C960 0 1200 0 1440 32V64H0V32Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
